/** Flight board helpers — mirrors PHP v4 helpers in index.php / flights-data.php */
import { withBasePath } from './paths.js';

/** Homepage v4 status chips */
const STATUS_MAP_V4 = {
  ontime: 'ontime',
  landed: 'landed',
  arrived: 'landed',
  scheduled: 'scheduled',
  boarding: 'boarding',
  baggage: 'boarding',
  finalcall: 'boarding',
  approaching: 'boarding',
  gate: 'boarding',
  delayed: 'delayed',
  newtime: 'delayed',
  departed: 'landed',
  cancelled: 'cancelled',
};

/** Full board status pills (mirrors PHP nkc_flight_status_class) */
const STATUS_MAP_BOARD = {
  ontime: 'ontime',
  landed: 'ontime',
  arrived: 'ontime',
  scheduled: 'scheduled',
  boarding: 'boarding',
  baggage: 'boarding',
  finalcall: 'gate',
  approaching: 'gate',
  gate: 'gate',
  delayed: 'delayed',
  newtime: 'delayed',
  departed: 'departed',
  cancelled: 'cancelled',
};

const DEFAULT_AIRLINE_LOGO = withBasePath('/assets/airlines/default.svg');

/** Bundled wordmark-style SVGs — prefer these over remote square blobs. */
const LOCAL_AIRLINE_LOGOS = new Set([
  'af',
  'ai',
  'ba',
  'cx',
  'et',
  'ia',
  'kl',
  'lh',
  'ms',
  'qr',
  'sa',
  'sq',
  'tk',
]);

function normalizeAirlineCode(code) {
  return String(code || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

/** IATA designator: two letters, or letter+digit / digit+letter (e.g. L6, 5X). */
export function isIataAirlineCode(code) {
  const c = normalizeAirlineCode(code);
  return /^[A-Z]{2}$/.test(c) || /^[A-Z][0-9]$/.test(c) || /^[0-9][A-Z]$/.test(c);
}

/**
 * @param {string} statusKey
 * @returns {string} e.g. 'v4-status v4-status--ontime'
 */
export function statusClass(statusKey) {
  const key = STATUS_MAP_V4[statusKey] ?? 'scheduled';
  return `v4-status v4-status--${key}`;
}

/**
 * @param {string} statusKey
 * @returns {string} e.g. 'status-pill status-pill--ontime'
 */
export function boardStatusClass(statusKey) {
  const key = STATUS_MAP_BOARD[statusKey] ?? 'scheduled';
  return `status-pill status-pill--${key}`;
}

/**
 * @param {string} statusKey
 * @returns {string} e.g. 'flight-table__row flight-table__row--ontime'
 */
export function boardRowClass(statusKey) {
  const key = STATUS_MAP_BOARD[statusKey] ?? 'scheduled';
  return `flight-table__row flight-table__row--${key}`;
}

/**
 * @param {object} flight
 * @param {'arr'|'dep'} dir
 * @param {'en'|'fr'|'ar'} [lang]
 * @returns {{ city: string, code: string }}
 */
export function placeLabel(flight, dir, lang = 'en') {
  const code =
    dir === 'dep'
      ? String(flight?.destinationCode ?? '')
      : String(flight?.originCode ?? '');

  let city = '';
  if (dir === 'dep') {
    if (lang === 'ar') city = flight?.destinationAr || flight?.destination;
    else if (lang === 'fr') city = flight?.destinationFr || flight?.destination;
    else city = flight?.destination;
  } else if (lang === 'ar') city = flight?.originAr || flight?.origin;
  else if (lang === 'fr') city = flight?.originFr || flight?.origin;
  else city = flight?.origin;

  return {
    city: String(city ?? ''),
    code,
  };
}

/**
 * Localized status label from API (status / status_fr / status_ar).
 * @param {object} flight
 * @param {'en'|'fr'|'ar'} [lang]
 */
export function localizedStatus(flight, lang = 'en') {
  if (lang === 'ar') return String(flight?.statusAr || flight?.statusEn || flight?.status || '—');
  if (lang === 'fr') return String(flight?.statusFr || flight?.statusEn || flight?.status || '—');
  return String(flight?.statusEn || flight?.status || '—');
}

/**
 * Prefer a local SVG when we have one (consistent rectangular mark).
 * Use Lenok remote logos only for real IATA codes. Codes like `_SIV` fall back
 * to the generic mark instead of the NKC airport placeholder.
 * @param {object} flight
 * @returns {string}
 */
export function airlineLogoSrc(flight) {
  const fromField = normalizeAirlineCode(flight?.airlineCode);
  const fromFlight = String(flight?.flight || '').trim().match(/^_?([A-Za-z0-9]{2,3})\b/);
  const code = (fromField || fromFlight?.[1] || '').toLowerCase();

  if (LOCAL_AIRLINE_LOGOS.has(code)) {
    return withBasePath(`/assets/airlines/${code}.svg`);
  }

  const remote = String(flight?.logoUrl ?? '').trim();
  if (isIataAirlineCode(code) && remote !== '' && /^https?:\/\//i.test(remote)) {
    return remote;
  }

  if (isIataAirlineCode(code) && code !== '') {
    return withBasePath(`/assets/airlines/${code}.svg`);
  }

  return DEFAULT_AIRLINE_LOGO;
}

export { DEFAULT_AIRLINE_LOGO };

/**
 * Lowercase haystack for board filter (flight, airline, place + IATA).
 * @param {object} flight
 * @param {'arr'|'dep'} dir
 * @returns {string}
 */
export function searchHaystack(flight, dir) {
  const parts =
    dir === 'dep'
      ? [
          flight?.flight,
          flight?.airline,
          flight?.destination,
          flight?.destinationFr,
          flight?.destinationAr,
          flight?.destinationCode,
          flight?.statusEn,
          flight?.statusFr,
          flight?.statusAr,
        ]
      : [
          flight?.flight,
          flight?.airline,
          flight?.origin,
          flight?.originFr,
          flight?.originAr,
          flight?.originCode,
          flight?.statusEn,
          flight?.statusFr,
          flight?.statusAr,
        ];
  return parts
    .map((p) => String(p ?? ''))
    .join(' ')
    .toLowerCase();
}

/**
 * Today's date as YYYY-MM-DD (UTC calendar date, matching PHP nkc_flight_today / GMT).
 * @returns {string}
 */
export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
